// Components==============
import { faFileCheck, faTrash } from '@fortawesome/pro-solid-svg-icons';
import Button from 'components/UI/Button/Button';
import CircleButton from 'components/UI/CircleButton/CircleButton';
import Input from 'components/UI/Form/Input';
import Textarea from 'components/UI/Form/Textarea';
import Tooltip from 'components/UI/Tooltip/Tooltip';
import TrainingCollapseCard from 'components/UI/TrainingCollapseCard/TrainingCollapseCard';
import TrainingContentCard from 'components/UI/TrainingContentCard/TrainingContentCard';
import { useTranslate } from 'hooks/useTranslate';
import {
  LessonType,
  TrainingBuilderContext,
} from 'pages/Workspace/TrainingBuilder/TrainingBuilder';
import React, { useContext, useEffect, useState } from 'react';
import { DragDropContext, Droppable } from 'react-beautiful-dnd';
import Content from './Content/Content';
import './LessonCard.module.scss';
// =========================

export default function LessonCard({
  lesson,
  index,
}: {
  lesson: LessonType;
  index: number;
}) {
  const {
    currentSession,
    setSessions,
    sessions,
    handleDeleteLesson,
    handleToggleEvidence,
    handleAddContentToLesson,
    handleDeleteLessonContent,
    handleReorderLessonContent,
  } = useContext(TrainingBuilderContext);

  const translate = useTranslate();

  function handleTitleChange(e: React.ChangeEvent<HTMLInputElement>) {
    setSessions((prev) =>
      prev.map((s) =>
        s.id === currentSession
          ? {
              ...s,
              lessons: s.lessons?.map((l) =>
                l.id === lesson.id
                  ? { ...l, title: e.target.value, changed: true }
                  : l
              ),
            }
          : s
      )
    );
  }

  function handleDescriptionChange(e: React.ChangeEvent<HTMLTextAreaElement>) {
    setSessions((prev) =>
      prev.map((s) =>
        s.id === currentSession
          ? {
              ...s,
              lessons: s.lessons?.map((l) =>
                l.id === lesson.id
                  ? { ...l, description: e.target.value, changed: true }
                  : l
              ),
            }
          : s
      )
    );
  }

  const [resizeTrigger, setResizeTrigger] = useState(false);
  useEffect(() => setResizeTrigger((prev) => !prev), [currentSession]);

  return (
    <TrainingCollapseCard
      left={
        <div styleName="top">
          <Input
            ext_onChange={handleTitleChange}
            ext_formValue={
              sessions
                .find((s) => s.id === currentSession)
                ?.lessons?.find((l) => l.id === lesson.id)?.title || ''
            }
            color="grey"
            placeholder={translate('general.terms.title')}
            name=""
          />
          <Tooltip content={translate('general.lesson-evidence-upload')}>
            <CircleButton
              icon={faFileCheck}
              color={lesson.evidence_required ? 'green' : 'grey'}
              onClick={() => handleToggleEvidence(lesson.id)}
            />
          </Tooltip>
          <CircleButton
            icon={faTrash}
            color="grey"
            onClick={() => handleDeleteLesson(lesson.id)}
          />
        </div>
      }
      lessonIndex={index}
    >
      <>
        <div styleName="description">
          <Textarea
            ext_formValue={
              sessions
                .find((s) => s.id === currentSession)
                ?.lessons?.find((l) => l.id === lesson.id)?.description || ''
            }
            ext_onChange={handleDescriptionChange}
            color="grey"
            placeholder={translate('general.terms.description')}
            name=""
            resizeTrigger={resizeTrigger}
          />
        </div>
        <div styleName="bar" />
        <p styleName="add-content">
          {translate('course.terms.lesson-content')}
        </p>
        <DragDropContext
          onDragEnd={(e) =>
            handleReorderLessonContent({ ...e, lessonId: lesson.id })
          }
        >
          <Droppable droppableId={`contents-${currentSession}-${lesson.id}`}>
            {(provided) => (
              <div
                {...provided.droppableProps}
                ref={provided.innerRef}
                styleName="content"
              >
                {lesson.contents
                  ?.sort((a, b) => a.order_index - b.order_index)
                  ?.map((content, i) => (
                    <TrainingContentCard
                      id={content.id}
                      index={i}
                      handleRemove={() =>
                        handleDeleteLessonContent({
                          lessonId: lesson.id,
                          contentId: content.id,
                        })
                      }
                      key={i}
                    >
                      <Content content={content} lesson={lesson} />
                    </TrainingContentCard>
                  ))}
                {provided.placeholder}
              </div>
            )}
          </Droppable>
        </DragDropContext>
        <div styleName="button-wrapper">
          <Button
            color="grey"
            onClick={() =>
              handleAddContentToLesson({ lessonId: lesson.id, type: 'image' })
            }
          >
            {translate('course.actions.add-photo')}
          </Button>
          <Button
            color="grey"
            onClick={() =>
              handleAddContentToLesson({ lessonId: lesson.id, type: 'text' })
            }
          >
            {translate('course.actions.add-text')}
          </Button>
          <Button
            color="grey"
            onClick={() =>
              handleAddContentToLesson({ lessonId: lesson.id, type: 'video' })
            }
          >
            {translate('course.actions.add-video')}
          </Button>
          <Button
            color="grey"
            onClick={() =>
              handleAddContentToLesson({
                lessonId: lesson.id,
                type: 'file',
              })
            }
          >
            {translate('course.actions.add-document')}
          </Button>
        </div>
      </>
    </TrainingCollapseCard>
  );
}
